import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import {
  applyRouteScroll,
  readRouteScroll,
  rememberRouteScroll,
  routeScrollHasHash,
  routeScrollIntent,
  routeScrollMemoryId,
  routeScrollMemorySearch,
  type RouteScrollBox,
} from '@marshal/shared';

function installManualScrollRestoration() {
  if (typeof history !== 'undefined' && 'scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }
}

// A route change commits the next page before useLayoutEffect runs. That
// scroll (often to 0) would overwrite the offset Back should restore.
let suppressScrollMemory = false;

installManualScrollRestoration();

function scrollTarget(hash: string): HTMLElement | null {
  const id = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!id) return null;
  try {
    return document.getElementById(decodeURIComponent(id));
  } catch {
    return document.getElementById(id);
  }
}

export function readScrollTop(el: HTMLElement | Window): number {
  return el instanceof Window ? el.scrollY : el.scrollTop;
}

function historyScrollId(): string {
  const state = (typeof history !== 'undefined' ? history.state : null) as {
    key?: string;
    idx?: number;
  } | null;
  const { pathname, search, hash } = window.location;
  return routeScrollMemoryId({
    historyKey: state?.key,
    historyIdx: state?.idx,
    pathname,
    search: routeScrollMemorySearch(search),
    hash,
  });
}

function withInstantScroll(run: () => void) {
  const root = document.documentElement;
  const previous = root.style.scrollBehavior;
  root.style.scrollBehavior = 'auto';
  run();
  // Restoring in the same turn lets Chrome keep a smooth scroll that started
  // when the new page clamped the previous offset down to the footer.
  requestAnimationFrame(() => {
    if (root.style.scrollBehavior === 'auto') root.style.scrollBehavior = previous;
  });
}

export function scrollBoxTo(el: HTMLElement | Window, top: number) {
  // Stylesheet `scroll-behavior: smooth` also smooths `behavior: 'auto'` in
  // current Chrome. A smooth scroll starts at the page we just left, so a
  // story opened from the footer stays on the footer until the animation ends.
  if (el instanceof Window) {
    withInstantScroll(() => el.scrollTo({ top, left: 0, behavior: 'auto' }));
    return;
  }
  el.scrollTop = top;
}

function maxScrollTop(el: HTMLElement | Window): number {
  if (el instanceof Window) {
    const root = document.scrollingElement || document.documentElement;
    return Math.max(0, root.scrollHeight - window.innerHeight);
  }
  return Math.max(0, el.scrollHeight - el.clientHeight);
}

/** Scroll an anchor inside `el` without moving unrelated scrollers. */
export function scrollBoxToHash(el: HTMLElement | Window, hash: string): boolean {
  const target = scrollTarget(hash);
  if (!target) return false;
  if (el instanceof Window) {
    withInstantScroll(() => target.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'auto' }));
    return true;
  }
  const margin = Number.parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
  const top = el.scrollTop + target.getBoundingClientRect().top - el.getBoundingClientRect().top - margin;
  el.scrollTop = Math.max(0, top);
  return true;
}

/**
 * Reset the page scroller on forward navigations and restore it on back/forward.
 * `scope` keeps the parent shell, the admin column, and the window separate.
 */
export function useRouteScroll(scope: string, getScroller: () => HTMLElement | Window | null) {
  const location = useLocation();
  const action = useNavigationType();
  const prev = useRef<{ pathname: string; search: string; hash: string; key: string } | null>(null);
  const getScrollerRef = useRef(getScroller);
  getScrollerRef.current = getScroller;
  const locationSig = `${location.key}|${location.pathname}${location.search}${location.hash}`;
  const sigRef = useRef(locationSig);
  if (sigRef.current !== locationSig) {
    suppressScrollMemory = true;
    sigRef.current = locationSig;
  }

  useEffect(() => {
    let attached: HTMLElement | Window | null = null;
    const onScroll = () => {
      if (!attached || suppressScrollMemory) return;
      // pushState/popstate already updated history.state, so a clamp on the
      // page we just entered is stored on that entry, not the one we left.
      rememberRouteScroll(scope, historyScrollId(), readScrollTop(attached));
    };
    const bind = () => {
      const next = getScrollerRef.current();
      if (next === attached) return;
      if (attached) attached.removeEventListener('scroll', onScroll);
      attached = next;
      if (attached) attached.addEventListener('scroll', onScroll, { passive: true });
    };
    bind();
    // The admin column mounts after the permissions spinner. Rebinding on
    // every render drops scroll events that are already queued.
    const timer = window.setInterval(bind, 200);
    const snapshot = () => {
      const node = getScrollerRef.current();
      if (!node) return;
      rememberRouteScroll(scope, historyScrollId(), readScrollTop(node));
    };
    document.addEventListener('pointerdown', snapshot, true);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('pointerdown', snapshot, true);
      if (attached) attached.removeEventListener('scroll', onScroll);
    };
  }, [scope]);

  useLayoutEffect(() => {
    installManualScrollRestoration();
    const next = {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      key: location.key,
      action,
    };
    const previous = prev.current;
    const current = getScrollerRef.current();
    const intent = routeScrollIntent(previous, next);
    const scrollId = historyScrollId();
    // Read while a route-change clamp is still ignored. Closing over this
    // number means a later clamp cannot replace the offset we retry with.
    const savedRestore = readRouteScroll(scope, scrollId);
    if (current) {
      const box: RouteScrollBox = {
        get scrollTop() {
          return readScrollTop(current);
        },
        set scrollTop(top: number) {
          scrollBoxTo(current, top);
        },
      };
      applyRouteScroll({
        intent,
        savedTop: savedRestore,
        hasHash: routeScrollHasHash(next.hash),
        box,
        tryAnchor: () => scrollBoxToHash(current, next.hash),
      });
    }
    suppressScrollMemory = false;
    prev.current = { pathname: next.pathname, search: next.search, hash: next.hash, key: next.key };

    let cancelled = false;
    let placedTop: number | null = null;
    const followUp = () => {
      if (cancelled || intent === 'keep') return;
      const node = getScrollerRef.current();
      if (!node) return;
      if (intent === 'anchor') {
        if (placedTop !== null && Math.abs(readScrollTop(node) - placedTop) > 8) return;
        if (scrollBoxToHash(node, next.hash)) placedTop = readScrollTop(node);
        return;
      }
      if (intent === 'restore') {
        if (savedRestore !== undefined) {
          const currentTop = readScrollTop(node);
          if (placedTop !== null && Math.abs(currentTop - placedTop) > 8) return;
          const limit = maxScrollTop(node);
          if (limit + 1 >= savedRestore && Math.abs(currentTop - Math.min(savedRestore, limit)) <= 1) return;
          // A short column clamps the saved offset to 0. Retry once it can hold it.
          suppressScrollMemory = true;
          scrollBoxTo(node, savedRestore);
          placedTop = readScrollTop(node);
          queueMicrotask(() => {
            suppressScrollMemory = false;
          });
          return;
        }
        if (routeScrollHasHash(next.hash)) {
          if (placedTop !== null && Math.abs(readScrollTop(node) - placedTop) > 8) return;
          if (scrollBoxToHash(node, next.hash)) placedTop = readScrollTop(node);
        }
        return;
      }
      scrollBoxTo(node, 0);
    };
    const frame = requestAnimationFrame(followUp);
    // Lazy routes (settings #api-keys, session boards) mount the target after
    // the first paint. Stop retrying once the reader has scrolled away.
    const retry =
      intent === 'anchor' ||
      (intent === 'restore' && (savedRestore !== undefined || routeScrollHasHash(next.hash)));
    const timers = retry ? [80, 240, 600].map((delay) => window.setTimeout(followUp, delay)) : [];
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      for (const timer of timers) window.clearTimeout(timer);
    };
  }, [action, location.hash, location.key, location.pathname, location.search, scope]);
}
